import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Briefcase, User, FileText, Bell, LogOut, Upload, X, Loader2,
  Sparkles, MapPin, CheckCircle2, AlertCircle, Settings, Mail,
  Building2, Heart, ExternalLink,
} from 'lucide-react';
import { JobsShell } from '../components/jobs/JobsShell';
import { useJobseekerAuth, formatApiError } from '../contexts/JobseekerAuthContext';

const ROLES = [
  'Dental Assistant', 'Registered Dental Assistant', 'Dental Hygienist',
  'Office Manager', 'Front Desk', 'Treatment Coordinator', 'Billing Specialist',
  'Orthodontic Assistant', 'Oral Surgery Assistant', 'Pediatric Dental Assistant',
  'Sterilization Technician',
];

const AVAILABILITIES = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'per_diem', label: 'Per-diem / Temp' },
  { value: 'any', label: 'Open to anything' },
];

export default function JobsDashboardPage() {
  const navigate = useNavigate();
  const { jobseeker, loading, logout, refreshMe, apiRequest } = useJobseekerAuth();
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    if (!loading && !jobseeker) navigate('/jobs/login');
  }, [loading, jobseeker, navigate]);

  if (loading || !jobseeker) {
    return (
      <JobsShell>
        <div className="max-w-3xl mx-auto py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700 mx-auto" />
        </div>
      </JobsShell>
    );
  }

  const greeting = jobseeker.firstName ? `Hi, ${jobseeker.firstName}` : 'Welcome';

  return (
    <JobsShell showSigninLink={false} showSignupLink={false}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="jobs-dashboard-page">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">{greeting} 👋</h1>
            <p className="text-slate-600 text-sm mt-1">
              Your DentalStaff profile · <span className="font-semibold">{jobseeker.email}</span>
            </p>
          </div>
          <button
            onClick={() => { logout(); navigate('/jobs'); }}
            data-testid="dashboard-logout-btn"
            className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border-2 border-slate-200 hover:border-rose-300 hover:text-rose-700 rounded-lg flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="flex border-b border-slate-200 overflow-x-auto">
            <TabBtn id="overview" active={tab} setActive={setTab} icon={<Sparkles className="w-4 h-4" />} label="Overview" />
            <TabBtn id="practices" active={tab} setActive={setTab} icon={<Building2 className="w-4 h-4" />} label="Practices Nearby" />
            <TabBtn id="profile"  active={tab} setActive={setTab} icon={<User className="w-4 h-4" />} label="Profile" />
            <TabBtn id="resume"   active={tab} setActive={setTab} icon={<FileText className="w-4 h-4" />} label="Resume" />
            <TabBtn id="presence" active={tab} setActive={setTab} icon={<Briefcase className="w-4 h-4" />} label="Where I Show Up" />
          </div>

          <div className="p-6 sm:p-8">
            {tab === 'overview' && <OverviewTab jobseeker={jobseeker} setTab={setTab} apiRequest={apiRequest} />}
            {tab === 'practices' && <PracticesTab apiRequest={apiRequest} jobseeker={jobseeker} />}
            {tab === 'profile' && <ProfileTab jobseeker={jobseeker} refreshMe={refreshMe} apiRequest={apiRequest} />}
            {tab === 'resume' && <ResumeTab jobseeker={jobseeker} refreshMe={refreshMe} apiRequest={apiRequest} />}
            {tab === 'presence' && <PresenceTab apiRequest={apiRequest} />}
          </div>
        </div>
      </div>
    </JobsShell>
  );
}

function TabBtn({ id, active, setActive, icon, label }) {
  const on = active === id;
  return (
    <button
      onClick={() => setActive(id)}
      data-testid={`dashboard-tab-${id}`}
      className={`px-5 py-4 text-sm font-bold flex items-center gap-2 whitespace-nowrap transition-colors ${
        on
          ? 'text-blue-700 border-b-2 border-blue-700 bg-blue-50/40'
          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-b-2 border-transparent'
      }`}
    >
      {icon} {label}
    </button>
  );
}

/* ============ OVERVIEW (warm empty state) ============ */
function OverviewTab({ jobseeker, setTab, apiRequest }) {
  const [alertOk, setAlertOk] = useState(false);
  const [alertErr, setAlertErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const hasResume = !!jobseeker.resumeFileId;
  const hasZip = !!jobseeker.zipCode;
  const hasRoles = (jobseeker.rolesInterested || []).length > 0;
  const profileComplete = hasResume && hasZip && hasRoles;

  const subscribeAlerts = async () => {
    setAlertErr('');
    setSubmitting(true);
    try {
      await apiRequest('post', '/api/jobs/alerts/subscribe', {
        email: jobseeker.email,
        zipCode: jobseeker.zipCode || '00000',
        rolesInterested: jobseeker.rolesInterested || [],
        radiusMiles: jobseeker.radiusMiles || 25,
      });
      setAlertOk(true);
    } catch (e) {
      setAlertErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="overview-tab">
      {/* Profile completion */}
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-bold text-slate-900">
              {profileComplete ? '✨ Your profile is complete!' : 'Let\'s finish setting up'}
            </h3>
            <p className="text-sm text-slate-600 mt-0.5">
              {profileComplete
                ? 'You\'re ready the moment matching jobs are posted.'
                : 'Practices want to see a full picture. Complete these and you\'ll be first in line.'}
            </p>
          </div>
        </div>
        <ul className="mt-4 space-y-2 text-sm">
          <ChecklistItem done={hasZip}    label="Add your ZIP code"     onClick={() => setTab('profile')} />
          <ChecklistItem done={hasRoles}  label="Pick your roles"        onClick={() => setTab('profile')} />
          <ChecklistItem done={hasResume} label="Upload your resume"     onClick={() => setTab('resume')} />
        </ul>
      </div>

      {/* Warm "no jobs yet" empty state */}
      <div className="mt-8 bg-white border-2 border-amber-200 rounded-xl p-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-full mb-4">
          <Bell className="w-3.5 h-3.5" /> BRAND NEW LISTING SERVICE
        </div>
        <Briefcase className="w-16 h-16 text-blue-700 mx-auto" />
        <h2 className="mt-4 text-2xl font-extrabold text-slate-900">
          We're just getting started in your area
        </h2>
        <p className="mt-3 text-slate-600 max-w-xl mx-auto">
          DentalStaff by PractCom is a brand-new service — there aren't any active openings yet.
          We're growing our network of dental practices every day. <strong className="text-slate-900">Please check back soon</strong>
          {jobseeker.city && jobseeker.state ? ` for future listings in ${jobseeker.city}, ${jobseeker.state}` : ' for future listings in your area'}.
          The moment a matching position opens, you'll be the first to know.
        </p>

        <div className="mt-5 p-3 bg-blue-50 border border-blue-200 rounded-lg max-w-xl mx-auto text-left">
          <p className="text-sm text-slate-700">
            <strong className="text-blue-900">💡 Keep your profile fresh!</strong> Practices see your latest info when they search.
            You can update your resume, contact details, roles, or availability anytime in the
            <button onClick={() => setTab('profile')} className="font-bold text-blue-700 hover:underline mx-1" data-testid="overview-go-profile-link">
              Profile
            </button>
            and
            <button onClick={() => setTab('resume')} className="font-bold text-blue-700 hover:underline mx-1" data-testid="overview-go-resume-link">
              Resume
            </button>
            tabs.
          </p>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3 text-sm text-slate-500">
          <Bell className="w-4 h-4 text-amber-500" />
          <span>You're automatically subscribed to alerts for the roles you picked.</span>
        </div>

        {!alertOk ? (
          <button
            onClick={subscribeAlerts}
            disabled={submitting}
            data-testid="overview-alert-confirm-btn"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white font-bold rounded-lg shadow-md"
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
              : <><CheckCircle2 className="w-4 h-4" /> Confirm my job alerts</>}
          </button>
        ) : (
          <p className="mt-6 inline-flex items-center gap-2 text-emerald-700 font-semibold" data-testid="overview-alert-ok">
            <CheckCircle2 className="w-5 h-5" /> You're on the list. We'll be in touch soon.
          </p>
        )}
        {alertErr && (
          <p className="mt-3 text-sm text-rose-700" data-testid="overview-alert-err">{alertErr}</p>
        )}
      </div>

      {/* Tips */}
      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        <TipCard icon={<MapPin className="w-5 h-5 text-blue-700" />} title="Stay in the loop"
                 body="We'll send an instant email when a matching job is posted within your radius." />
        <TipCard icon={<FileText className="w-5 h-5 text-cyan-600" />} title="Resume = first impression"
                 body="Practices see your resume the second you apply. A current PDF goes a long way." />
        <TipCard icon={<Mail className="w-5 h-5 text-emerald-600" />} title="100% free, always"
                 body="We charge practices to post, not you. Zero fees, zero spam, zero data resale." />
      </div>
    </div>
  );
}

function ChecklistItem({ done, label, onClick }) {
  return (
    <li className="flex items-center justify-between">
      <span className="flex items-center gap-2">
        {done
          ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          : <AlertCircle className="w-5 h-5 text-amber-500" />}
        <span className={done ? 'text-slate-700 line-through' : 'text-slate-900 font-semibold'}>{label}</span>
      </span>
      {!done && (
        <button onClick={onClick} className="text-xs font-bold text-blue-700 hover:underline">
          Add now →
        </button>
      )}
    </li>
  );
}

function TipCard({ icon, title, body }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
      <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center">{icon}</div>
      <p className="mt-3 font-bold text-slate-900">{title}</p>
      <p className="text-sm text-slate-600 mt-1">{body}</p>
    </div>
  );
}

/* ============ PROFILE TAB ============ */
function ProfileTab({ jobseeker, refreshMe, apiRequest }) {
  const [form, setForm] = useState({
    firstName: jobseeker.firstName || '',
    lastName: jobseeker.lastName || '',
    phone: jobseeker.phone || '',
    zipCode: jobseeker.zipCode || '',
    yearsExperience: jobseeker.yearsExperience ?? '',
    availability: jobseeker.availability || 'any',
    desiredPayMin: jobseeker.desiredPayMin ?? '',
    radiusMiles: jobseeker.radiusMiles || 25,
    rolesInterested: jobseeker.rolesInterested || [],
    bio: jobseeker.bio || '',
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleRole = (r) =>
    set('rolesInterested',
        form.rolesInterested.includes(r)
          ? form.rolesInterested.filter((x) => x !== r)
          : [...form.rolesInterested, r]);

  const save = async () => {
    setErr('');
    setSaving(true);
    try {
      await apiRequest('patch', '/api/jobs/auth/me', {
        ...form,
        yearsExperience: form.yearsExperience ? Number(form.yearsExperience) : null,
        desiredPayMin: form.desiredPayMin ? Number(form.desiredPayMin) : null,
        radiusMiles: Number(form.radiusMiles) || 25,
        zipCode: (form.zipCode || '').trim() || null,
      });
      setSavedAt(new Date());
      await refreshMe();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="profile-tab">
      <h2 className="text-xl font-extrabold text-slate-900">Your profile</h2>
      <p className="text-sm text-slate-600 mt-1">
        Keep this current — it's what practices see when you apply.
      </p>

      {err && <div className="mt-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg">{err}</div>}

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <PField label="First name"><input className="input-base" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} data-testid="profile-firstname" /></PField>
        <PField label="Last name"><input className="input-base" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} data-testid="profile-lastname" /></PField>
        <PField label="Phone"><input className="input-base" value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="profile-phone" /></PField>
        <PField label="ZIP code">
          <input
            className="input-base"
            inputMode="numeric"
            maxLength={5}
            value={form.zipCode}
            onChange={(e) => set('zipCode', e.target.value.replace(/\D/g, ''))}
            data-testid="profile-zip"
          />
        </PField>
        <PField label="Years experience"><input type="number" min="0" max="60" className="input-base" value={form.yearsExperience} onChange={(e) => set('yearsExperience', e.target.value)} data-testid="profile-years" /></PField>
        <PField label="Desired hourly rate (USD)"><input type="number" min="0" className="input-base" value={form.desiredPayMin} onChange={(e) => set('desiredPayMin', e.target.value)} data-testid="profile-pay" /></PField>
        <PField label="Search radius (miles)" className="sm:col-span-2">
          <input type="range" min="5" max="100" step="5" value={form.radiusMiles} onChange={(e) => set('radiusMiles', e.target.value)} className="w-full" data-testid="profile-radius" />
          <p className="text-sm text-slate-600">Within <span className="font-bold text-blue-700">{form.radiusMiles} miles</span> of your ZIP</p>
        </PField>
        <PField label="Availability" className="sm:col-span-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {AVAILABILITIES.map((a) => (
              <button
                key={a.value}
                type="button"
                onClick={() => set('availability', a.value)}
                data-testid={`profile-avail-${a.value}`}
                className={`px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${
                  form.availability === a.value
                    ? 'bg-blue-50 border-blue-600 text-blue-700'
                    : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </PField>
        <PField label="Roles you're interested in" className="sm:col-span-2">
          <div className="flex flex-wrap gap-2">
            {ROLES.map((r) => {
              const on = form.rolesInterested.includes(r);
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => toggleRole(r)}
                  data-testid={`profile-role-${r.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-colors ${
                    on
                      ? 'bg-cyan-50 border-cyan-500 text-cyan-800'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                  }`}
                >
                  {r}
                </button>
              );
            })}
          </div>
        </PField>
        <PField label="About you (optional)" className="sm:col-span-2">
          <textarea
            rows={4}
            className="input-base"
            value={form.bio}
            onChange={(e) => set('bio', e.target.value)}
            placeholder="A short note practices should see — what you love about dentistry, any specialties, etc."
            data-testid="profile-bio"
          />
        </PField>
      </div>

      <div className="mt-8 flex items-center gap-4">
        <button
          onClick={save}
          disabled={saving}
          data-testid="profile-save-btn"
          className="px-6 py-3 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-bold rounded-lg shadow-md flex items-center gap-2"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Settings className="w-4 h-4" /> Save changes</>}
        </button>
        {savedAt && (
          <span className="text-sm text-emerald-700 font-semibold flex items-center gap-1" data-testid="profile-saved-flag">
            <CheckCircle2 className="w-4 h-4" /> Saved
          </span>
        )}
      </div>
      <style>{`
        .input-base {
          width: 100%; padding: 10px 12px; border: 2px solid #e2e8f0;
          border-radius: 10px; font-size: 15px; background: #fff;
        }
        .input-base:focus { outline: none; border-color: #2563eb;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.12); }
      `}</style>
    </div>
  );
}

function PField({ label, className = '', children }) {
  return (
    <div className={className}>
      <label className="block text-xs font-bold uppercase tracking-wide text-slate-700 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

/* ============ RESUME TAB ============ */
function ResumeTab({ jobseeker, refreshMe, apiRequest }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const handleFile = async (e) => {
    setErr('');
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErr('File is too large. Max size is 5 MB.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await apiRequest('post', '/api/jobs/me/resume', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshMe();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setUploading(false);
    }
  };

  const removeResume = async () => {
    if (!window.confirm('Remove your current resume?')) return;
    try {
      await apiRequest('delete', '/api/jobs/me/resume');
      await refreshMe();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  return (
    <div data-testid="resume-tab">
      <h2 className="text-xl font-extrabold text-slate-900">Your resume</h2>
      <p className="text-sm text-slate-600 mt-1">PDF or DOCX, up to 5 MB. Replace any time — only the latest is sent.</p>

      {err && <div className="mt-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg" data-testid="resume-tab-error">{err}</div>}

      {jobseeker.resumeFileId ? (
        <div className="mt-6 p-5 bg-emerald-50 border-2 border-emerald-200 rounded-xl flex items-center justify-between gap-4 flex-wrap" data-testid="resume-current">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-10 h-10 text-emerald-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-bold text-slate-900 truncate">{jobseeker.resumeFilename || 'resume'}</p>
              <p className="text-xs text-slate-500">
                Uploaded {jobseeker.resumeUploadedAt ? new Date(jobseeker.resumeUploadedAt).toLocaleString() : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label
              htmlFor="resume-replace"
              className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-lg cursor-pointer flex items-center gap-2"
              data-testid="resume-replace-btn"
            >
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Replace</>}
              <input id="resume-replace" type="file" className="hidden"
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFile} data-testid="resume-replace-input"
              />
            </label>
            <button
              onClick={removeResume}
              className="px-4 py-2 bg-white border-2 border-rose-200 text-rose-700 hover:bg-rose-50 text-sm font-bold rounded-lg flex items-center gap-2"
              data-testid="resume-remove-btn"
            >
              <X className="w-4 h-4" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <label
          htmlFor="resume-first-upload"
          className="mt-6 block border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
          data-testid="resume-tab-dropzone"
        >
          <Upload className="w-10 h-10 text-blue-700 mx-auto" />
          <p className="mt-3 font-bold text-slate-900">{uploading ? 'Uploading…' : 'Click to upload your resume'}</p>
          <p className="text-xs text-slate-500 mt-1">PDF or DOCX · max 5 MB</p>
          <input
            id="resume-first-upload"
            type="file"
            className="hidden"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleFile}
            data-testid="resume-tab-file-input"
          />
        </label>
      )}
    </div>
  );
}

/* ============ PRACTICES NEARBY TAB ============ */
function PracticesTab({ apiRequest, jobseeker }) {
  const [practices, setPractices] = useState(null);
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [radius, setRadius] = useState(jobseeker.radiusMiles || 25);

  const load = useCallback(async () => {
    setErr('');
    try {
      const res = await apiRequest('get', `/api/jobs/practices/nearby?radius_miles=${radius}`);
      setPractices(res.data.practices || []);
    } catch (e) {
      setErr(formatApiError(e));
      setPractices([]);
    }
  }, [apiRequest, radius]);

  useEffect(() => { load(); }, [load]);

  const expressInterest = async (p) => {
    setBusyId(p.id);
    setErr('');
    try {
      await apiRequest('post', `/api/jobs/practices/${p.id}/interest`);
      // refresh
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const hasResume = !!jobseeker.resumeFileId;

  return (
    <div data-testid="practices-tab">
      <h2 className="text-xl font-extrabold text-slate-900">Practices near you</h2>
      <p className="text-sm text-slate-600 mt-1">
        Tap <span className="font-bold text-rose-600">♥ I'd love to work here</span> to jump to the top of a practice's applicant inbox.
      </p>

      {!hasResume && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-300 text-amber-900 text-sm rounded-lg" data-testid="practices-need-resume">
          Upload a resume first — practices won't see your application without one.
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <label className="text-xs font-bold uppercase tracking-wide text-slate-700">Radius (miles)</label>
        <input
          type="range" min="5" max="100" step="5"
          value={radius}
          onChange={(e) => setRadius(Number(e.target.value))}
          className="flex-1 max-w-[240px]"
          data-testid="practices-radius-slider"
        />
        <span className="text-sm font-bold text-blue-700">{radius} mi</span>
      </div>

      {err && <div className="mt-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg">{err}</div>}

      {practices === null ? (
        <div className="mt-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-blue-700 mx-auto" /></div>
      ) : practices.length === 0 ? (
        <div className="mt-8 p-8 bg-slate-50 border border-slate-200 rounded-xl text-center" data-testid="practices-empty">
          <Building2 className="w-12 h-12 text-slate-400 mx-auto" />
          <p className="mt-3 font-bold text-slate-900">No PractCom practices within {radius} miles yet</p>
          <p className="text-sm text-slate-600 mt-1">Try expanding your radius. We're growing fast!</p>
        </div>
      ) : (
        <ul className="mt-6 space-y-3" data-testid="practices-list">
          {practices.map((p) => (
            <li key={p.id}
                data-testid={`practice-card-${p.id}`}
                className="p-4 bg-white border-2 border-slate-200 hover:border-blue-300 rounded-xl flex items-center justify-between gap-4 flex-wrap transition-colors">
              <div className="min-w-0">
                <p className="font-bold text-slate-900 truncate">{p.name}</p>
                <p className="text-sm text-slate-600 flex items-center gap-1.5 mt-0.5">
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                  {p.city}, {p.state} · {p.distanceMiles} mi away
                </p>
                {p.hiringRoles?.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    Often hiring: {p.hiringRoles.join(', ')}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {p.website && (
                  <a href={p.website} target="_blank" rel="noreferrer"
                     className="px-3 py-2 text-xs text-slate-600 hover:text-slate-900 font-semibold flex items-center gap-1"
                     data-testid={`practice-website-${p.id}`}>
                    Website <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                {p.alreadyInterested ? (
                  <span data-testid={`practice-interested-flag-${p.id}`}
                        className="px-4 py-2 bg-rose-50 text-rose-700 font-bold text-sm rounded-lg flex items-center gap-1.5 border-2 border-rose-200">
                    <Heart className="w-4 h-4 fill-rose-600 text-rose-600" /> Interest sent
                  </span>
                ) : (
                  <button
                    onClick={() => expressInterest(p)}
                    disabled={busyId === p.id || !hasResume}
                    data-testid={`practice-interest-btn-${p.id}`}
                    className="px-4 py-2 bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 disabled:opacity-50 text-white font-bold text-sm rounded-lg shadow-sm flex items-center gap-1.5"
                  >
                    {busyId === p.id
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                      : <><Heart className="w-4 h-4" /> I'd love to work here</>}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============ WHERE I SHOW UP TAB ============ */
function PresenceTab({ apiRequest }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await apiRequest('get', '/api/jobs/me/inbox-presence');
        setRows(res.data.presence || []);
      } catch (e) {
        setErr(formatApiError(e));
        setRows([]);
      }
    })();
  }, [apiRequest]);

  return (
    <div data-testid="presence-tab">
      <h2 className="text-xl font-extrabold text-slate-900">Where your profile is showing up</h2>
      <p className="text-sm text-slate-600 mt-1">
        These practices have your resume in their applicant inbox right now.
      </p>

      {err && <div className="mt-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg">{err}</div>}

      {rows === null ? (
        <div className="mt-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-blue-700 mx-auto" /></div>
      ) : rows.length === 0 ? (
        <div className="mt-8 p-8 bg-slate-50 border border-slate-200 rounded-xl text-center" data-testid="presence-empty">
          <Briefcase className="w-12 h-12 text-slate-400 mx-auto" />
          <p className="mt-3 font-bold text-slate-900">Not in any inboxes yet</p>
          <p className="text-sm text-slate-600 mt-1">
            Make sure your ZIP, roles, and resume are all set on your Profile and Resume tabs.
          </p>
        </div>
      ) : (
        <ul className="mt-6 space-y-3" data-testid="presence-list">
          {rows.map((r) => (
            <li key={r.id}
                data-testid={`presence-row-${r.id}`}
                className="p-4 bg-white border border-slate-200 rounded-xl flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="font-bold text-slate-900">{r.practice?.name || 'Practice'}</p>
                <p className="text-sm text-slate-600">
                  {r.practice?.city}, {r.practice?.state}
                  {r.distanceMiles != null && ` · ${r.distanceMiles} mi`}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Since {new Date(r.appliedAt).toLocaleDateString()}
                  {r.source === 'dentalstaff_interest' && ' · ♥ You expressed interest'}
                </p>
              </div>
              <StatusPill status={r.status} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new:         { c: 'bg-blue-50 text-blue-700 border-blue-200',          t: 'In inbox' },
    reviewed:    { c: 'bg-indigo-50 text-indigo-700 border-indigo-200',    t: 'Reviewed' },
    shortlisted: { c: 'bg-emerald-50 text-emerald-700 border-emerald-200', t: 'Shortlisted' },
    hired:       { c: 'bg-emerald-100 text-emerald-800 border-emerald-300',t: 'Hired 🎉' },
    rejected:    { c: 'bg-slate-100 text-slate-600 border-slate-200',      t: 'Closed' },
  };
  const cfg = map[status] || map.new;
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${cfg.c}`}>
      {cfg.t}
    </span>
  );
}
